import http from './http';

export type SchedulerUser = {
  id: string;
  name: string | null;
  email: string;
  is_active: boolean;
  schedule_count: number;
};

export type SchedulerJobType = {
  job_type: string;
  label: string;
  description: string;
  default_cron: string;
  supports_global: boolean;
};

export type SchedulerJob = {
  schedule_id: string;
  job_id: string;
  name: string;
  job_type: string;
  job_type_label: string;
  user_id: string | null;
  user_name: string | null;
  user_email: string | null;
  scope: 'global' | 'user';
  cron_expression: string;
  timezone: string;
  trigger: string;
  is_enabled: boolean;
  is_system: boolean;
  next_run: string | null;
  last_status: string;
  last_started: string | null;
  last_finished: string | null;
  last_error: string | null;
  created_at: string | null;
  updated_at: string | null;
};

export type SchedulerStatus = {
  scheduler_running: boolean;
  jobs: SchedulerJob[];
};

export type ScheduleInput = {
  name: string;
  job_type: string;
  user_id: string | null;
  cron_expression: string;
  timezone: string;
  is_enabled: boolean;
};

export type JobRunHistory = {
  id: number;
  scheduled_job_id: string | null;
  user_id: string | null;
  job_name: string;
  trigger_source: 'scheduled' | 'manual';
  status: string;
  started_at: string;
  finished_at: string | null;
  error_message: string | null;
  result_summary: string | null;
};

export type UserSchedulerJob = {
  schedule_id: string;
  name: string;
  job_type: string;
  job_type_label: string;
  cron_expression: string;
  timezone: string;
  is_enabled: boolean;
  next_run: string | null;
  last_status: string;
  last_started: string | null;
  last_error: string | null;
  created_at: string | null;
};

export type UserSchedulerStatus = {
  scheduler_running: boolean;
  jobs: UserSchedulerJob[];
};

const BASE = '/scheduler';

export const schedulerApi = {
  getStatus: (params?: { user_id?: string; scope?: 'all' | 'global' | 'user' }) =>
    http.get<SchedulerStatus>(`${BASE}/status`, { params }).then(r => r.data),

  getUsers: (search?: string) =>
    http.get<SchedulerUser[]>(`${BASE}/users`, { params: { search } }).then(r => r.data),

  getJobTypes: () =>
    http.get<SchedulerJobType[]>(`${BASE}/job-types`).then(r => r.data),

  createSchedule: (payload: ScheduleInput) =>
    http.post<SchedulerJob>(`${BASE}/schedules`, payload).then(r => r.data),

  updateSchedule: (scheduleId: string, payload: Partial<ScheduleInput>) =>
    http.put<SchedulerJob>(`${BASE}/schedules/${scheduleId}`, payload).then(r => r.data),

  deleteSchedule: (scheduleId: string) =>
    http.delete(`${BASE}/schedules/${scheduleId}`),

  triggerJob: (scheduleId: string) =>
    http.post<{ ok: boolean; message: string }>(`${BASE}/trigger/${scheduleId}`).then(r => r.data),

  getHistory: (scheduledJobId?: string, limit = 20) =>
    http.get<JobRunHistory[]>(`${BASE}/history`, {
      params: { scheduled_job_id: scheduledJobId, limit },
    }).then(r => r.data),

  // ── User self-service (view + delete own jobs only) ──
  getMyJobs: () =>
    http.get<UserSchedulerStatus>(`${BASE}/me/jobs`).then(r => r.data),

  deleteMyJob: (scheduleId: string) =>
    http.delete(`${BASE}/me/jobs/${scheduleId}`),
};
